import { API_BASE_URL } from './api';
import { getAccessToken } from './auth';

export const GRADIENT_PRESET_LIMIT = 20;

export interface GradientPreset {
  id: string;
  colors: string[];
  createdAt: string;
}

export interface SaveGradientPresetResult {
  preset: GradientPreset;
  created: boolean;
}

const GRADIENT_PRESETS_URL = `${API_BASE_URL}/me/gradient-presets`;
const HEX_COLOR_PATTERN = /^#[0-9a-f]{6}$/i;

export async function loadGradientPresets(): Promise<GradientPreset[]> {
  const response = await authFetch(GRADIENT_PRESETS_URL, {
    method: 'GET',
    cache: 'no-store'
  });
  const payload = (await response.json()) as { presets?: unknown };
  return normalizeGradientPresets(payload.presets);
}

export async function saveGradientPreset(colors: string[]): Promise<SaveGradientPresetResult> {
  const response = await authFetch(GRADIENT_PRESETS_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({ colors })
  });
  const payload = (await response.json()) as { preset?: unknown; created?: unknown };
  const preset = normalizeGradientPreset(payload.preset);
  if (!preset) {
    throw new Error('The saved palette response is invalid.');
  }
  return {
    preset,
    created: payload.created === true
  };
}

export async function deleteGradientPreset(id: string): Promise<void> {
  const presetId = id.trim();
  if (!presetId) {
    throw new Error('Palette id is missing.');
  }
  await authFetch(`${GRADIENT_PRESETS_URL}/${encodeURIComponent(presetId)}`, {
    method: 'DELETE'
  });
}

function normalizeGradientPresets(value: unknown): GradientPreset[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value
    .map(normalizeGradientPreset)
    .filter((preset): preset is GradientPreset => Boolean(preset))
    .slice(0, GRADIENT_PRESET_LIMIT);
}

function normalizeGradientPreset(value: unknown): GradientPreset | null {
  if (!value || typeof value !== 'object') {
    return null;
  }
  const candidate = value as Record<string, unknown>;
  const id = typeof candidate.id === 'string' ? candidate.id.trim() : '';
  const createdAt = typeof candidate.createdAt === 'string' ? candidate.createdAt.trim() : '';
  const colors = Array.isArray(candidate.colors)
    ? candidate.colors.map((color) => String(color).trim().toLowerCase())
    : [];
  if (!id || colors.length !== 5 || colors.some((color) => !HEX_COLOR_PATTERN.test(color))) {
    return null;
  }
  return { id, colors, createdAt };
}

async function authFetch(url: string, init: RequestInit): Promise<Response> {
  const accessToken = await getAccessToken();
  if (!accessToken) {
    throw new Error('Sign in is required.');
  }

  const response = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      ...(init.headers || {})
    }
  });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response, 'Palette request failed.'));
  }
  return response;
}

async function readErrorMessage(response: Response, fallback: string): Promise<string> {
  try {
    const payload = (await response.json()) as { error?: unknown };
    return typeof payload.error === 'string' && payload.error.trim() ? payload.error.trim() : fallback;
  } catch {
    return fallback;
  }
}
